/**
 * Footer Component
 *
 * Collapsible footer fixed to the bottom of the viewport. A slim handle is
 * always visible; clicking it toggles the body open/closed. State persists
 * in localStorage so the user's preference sticks across sessions. Default
 * is collapsed — the goal is for the footer never to be intrusive.
 *
 * Auto-collapse triggers (only fire while expanded):
 *   - Any pointerdown outside the footer.
 *   - Any window scroll.
 *   - IDLE_TIMEOUT_MS without interaction inside the footer (resets on any
 *     pointer/touch/mouse move within the footer element).
 * These exist because users habitually expand the footer and forget about
 * it, leaving it covering the wizard's Next/Previous buttons.
 *
 * Copyright line, links and disclaimer are admin-configurable per retailer
 * (Admin → Menu, Splash & Footer → Footer; site_experience_configs.footer_json).
 */

import { useTranslation } from 'react-i18next';
import React, { useState, useEffect, useRef, useMemo } from 'react';
import DOMPurify from 'dompurify';
import { VERSION_LABEL } from '../../version';
import { useSiteExperience } from '../../context/SiteExperienceContext';
import { BUILTIN_FOOTER_LINKS, SiteFooterLink } from '../../config/siteExperience';
import './Footer.css';

const STORAGE_KEY = 'tcFooterCollapsed';
const IDLE_TIMEOUT_MS = 6000;

export const Footer: React.FC = () => {
  const { t } = useTranslation('common');
  const currentYear = new Date().getFullYear();
  // Admin-set fields (Trusscore default, then the retailer's) win; anything
  // left unset falls back to the built-in footer below.
  const { footer } = useSiteExperience().config;
  const links: SiteFooterLink[] = footer.links
    ?? BUILTIN_FOOTER_LINKS.map(l => ({ id: l.id, label: t(`footer.${l.labelKey}`), url: l.url, target: '_blank' }));
  const disclaimerHtml = useMemo(
    () => footer.disclaimerHtml !== undefined ? DOMPurify.sanitize(footer.disclaimerHtml, { ADD_ATTR: ['target'] }) : null,
    [footer.disclaimerHtml],
  );
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored === null ? true : stored === '1';
    } catch {
      return true;
    }
  });
  const footerRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, collapsed ? '1' : '0');
    } catch {
      /* ignore storage errors (private mode, etc.) */
    }
  }, [collapsed]);

  // Auto-collapse plumbing — wired up only while the footer is actually
  // expanded so we don't pay listener cost on every scroll/click in the
  // common (collapsed) case.
  useEffect(() => {
    if (collapsed) return;

    let idleTimer: number | undefined;
    const collapse = () => setCollapsed(true);

    const armIdle = () => {
      if (idleTimer !== undefined) window.clearTimeout(idleTimer);
      idleTimer = window.setTimeout(collapse, IDLE_TIMEOUT_MS);
    };

    const onPointerDown = (e: PointerEvent) => {
      if (!footerRef.current) return;
      if (!footerRef.current.contains(e.target as Node)) {
        // Click landed outside the footer — close immediately.
        collapse();
      }
    };

    const onScroll = () => collapse();

    const onFooterActivity = () => armIdle();

    document.addEventListener('pointerdown', onPointerDown, true);
    window.addEventListener('scroll', onScroll, { passive: true });
    const node = footerRef.current;
    node?.addEventListener('pointermove', onFooterActivity);
    node?.addEventListener('pointerdown', onFooterActivity);
    node?.addEventListener('keydown', onFooterActivity);

    armIdle();

    return () => {
      if (idleTimer !== undefined) window.clearTimeout(idleTimer);
      document.removeEventListener('pointerdown', onPointerDown, true);
      window.removeEventListener('scroll', onScroll);
      node?.removeEventListener('pointermove', onFooterActivity);
      node?.removeEventListener('pointerdown', onFooterActivity);
      node?.removeEventListener('keydown', onFooterActivity);
    };
  }, [collapsed]);

  return (
    <footer ref={footerRef} className={`footer${collapsed ? ' footer--collapsed' : ''}`}>
      <button
        type="button"
        className="footer-handle"
        onClick={() => setCollapsed(c => !c)}
        aria-label={collapsed ? t('footer.expand') : t('footer.collapse')}
        aria-expanded={!collapsed}
      >
        <svg
          viewBox="0 0 24 24"
          width="20"
          height="20"
          fill="currentColor"
          aria-hidden="true"
          className="footer-handle-icon"
        >
          {/* Up triangle */}
          <path d="M12 4 6 11 H18 Z" />
          {/* Down triangle (gap from y=11 to y=13) */}
          <path d="M12 20 6 13 H18 Z" />
        </svg>
      </button>

      <div className="footer-body">
        <div className="footer-container">
          <div className="footer-content">
            <div className="footer-brand">
              <p className="footer-copyright small mb-0">
                {footer.copyright !== undefined
                  ? footer.copyright.split('{year}').join(String(currentYear))
                  : t('footer.copyright', { year: currentYear })}
              </p>
            </div>

            {links.length > 0 && (
              <div className="footer-links small">
                {links.map(link => (
                  <a
                    key={link.id}
                    href={link.url}
                    target={link.target}
                    rel={link.target === '_blank' ? 'noopener noreferrer' : undefined}
                    className="footer-link"
                  >
                    {link.label}
                  </a>
                ))}
              </div>
            )}
          </div>

          <div className="footer-disclaimer">
            {disclaimerHtml !== null ? (
              disclaimerHtml && <div className="footer-disclaimer-html small" dangerouslySetInnerHTML={{ __html: disclaimerHtml }} />
            ) : (
              <p className="small">
                {t('footer.disclaimer')}
              </p>
            )}
            <p className="footer-build small">v{VERSION_LABEL}</p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
